import { CSSProperties, KeyboardEvent, useId, useRef, useState } from 'react';

/** Id of the built-in "Other" chip. Never collides with a database id or slug. */
export const OTHER_CHOICE_ID = '__other__';

export interface ChoiceOption {
  id: string;
  label: string;
  /** Shown but not pickable (for example a city that isn't open yet). */
  disabled?: boolean;
}

/**
 * ChoiceChips — tap-to-answer chips for Alma's question sheet.
 *
 * Modes:
 *   single  → one pick (role="radiogroup"). Arrow keys move focus, Enter/Space
 *             picks — picking can advance the flow, so arrows never pick.
 *   multi   → any number of picks, unordered (aria-pressed).
 *   ordered → any number of picks; tap order is the order, shown as a serif
 *             number badge. Removing one closes the gap.
 *
 * `value` is the picked ids in order. With `other`, a trailing "Other" chip
 * (OTHER_CHOICE_ID) opens an inline text field on a hairline; it takes a
 * position like any option. Whether an empty "Other" counts is the caller's
 * call.
 *
 * Selection never changes weight — only fill and text color.
 */
export interface ChoiceChipsProps {
  options: ChoiceOption[];
  mode: 'single' | 'multi' | 'ordered';
  /** Picked ids, in pick order. */
  value: string[];
  onChange: (ids: string[]) => void;
  /** Accessible name for the group (usually the sheet title). */
  label: string;
  /** Word used when announcing an ordered pick: "Lisbon, stop 1" / "Food, ranked 1". Default "ranked". */
  positionWord?: string;
  /** Adds an "Other" chip with a free-text field. */
  other?: {
    text: string;
    onTextChange: (text: string) => void;
    /** Chip label. Default "Other". */
    label?: string;
    /** Field placeholder. Default "Tell us". */
    placeholder?: string;
  };
  /** Skeleton chips while the options load. */
  loading?: boolean;
  /** Load failure: plain message plus a retry. Replaces the chips. */
  error?: { message: string; onRetry: () => void };
  style?: CSSProperties;
}

const SKELETON_WIDTHS = [92, 128, 76, 110, 140, 84, 118];

export function ChoiceChips({
  options,
  mode,
  value,
  onChange,
  label,
  positionWord = 'ranked',
  other,
  loading = false,
  error,
  style,
}: ChoiceChipsProps) {
  const otherFieldId = useId();
  const chipRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [otherFocus, setOtherFocus] = useState(false);

  if (error) {
    return (
      <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', ...style }}>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--text)' }}>{error.message}</span>
        <button
          type="button"
          onClick={error.onRetry}
          className="sk-flow-focus"
          style={{
            height: 36, padding: '0 15px', borderRadius: 'var(--r-pill)', border: 'none',
            background: 'var(--chip-bg)', color: 'var(--text)',
            fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div aria-busy="true" aria-label={`${label}, loading`} style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 6px', ...style }}>
        {SKELETON_WIDTHS.map((w, i) => (
          <span key={i} style={{ width: w, height: 36, borderRadius: 'var(--r-pill)', background: 'var(--chip-bg)' }} />
        ))}
      </div>
    );
  }

  const all: ChoiceOption[] = other ? [...options, { id: OTHER_CHOICE_ID, label: other.label ?? 'Other' }] : options;
  const otherPicked = value.includes(OTHER_CHOICE_ID);

  function toggle(id: string) {
    if (mode === 'single') {
      onChange([id]);
      return;
    }
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  }

  // Roving focus for the single-choice radiogroup: one tab stop, arrows move.
  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (mode !== 'single') return;
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    chipRefs.current[(index + step + all.length) % all.length]?.focus();
  }

  const focusIndex = mode === 'single' ? Math.max(0, all.findIndex((o) => value.includes(o.id) && !o.disabled)) : -1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, ...style }}>
      <div
        role={mode === 'single' ? 'radiogroup' : 'group'}
        aria-label={label}
        style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 6px' }}
      >
        {all.map((option, i) => {
          const position = value.indexOf(option.id);
          const selected = position !== -1;
          const showBadge = mode === 'ordered' && selected;
          const a11yLabel = mode === 'ordered' && selected
            ? `${option.label}, ${positionWord} ${position + 1}. Tap to remove.`
            : undefined;
          return (
            <button
              key={option.id}
              ref={(el) => { chipRefs.current[i] = el; }}
              type="button"
              onClick={() => toggle(option.id)}
              disabled={option.disabled}
              onKeyDown={(e) => onKeyDown(e, i)}
              className="sk-flow-focus"
              {...(mode === 'single'
                ? { role: 'radio', 'aria-checked': selected, tabIndex: i === focusIndex ? 0 : -1 }
                : { 'aria-pressed': selected })}
              aria-label={a11yLabel}
              aria-controls={option.id === OTHER_CHOICE_ID && selected ? otherFieldId : undefined}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                // 36px tall on one line; a label too long for the row wraps
                // inside the chip instead of being cut off.
                minHeight: 36,
                padding: showBadge ? '8px 15px 8px 8px' : '8px 15px',
                borderRadius: 'var(--r-pill)',
                border: 'none',
                background: selected ? 'var(--accent)' : 'var(--chip-bg)',
                color: selected ? 'var(--on-accent)' : 'var(--chip-text)',
                fontFamily: 'var(--font-sans)',
                fontSize: 13,
                fontWeight: 500,
                lineHeight: 1.25,
                textAlign: 'left',
                cursor: option.disabled ? 'not-allowed' : 'pointer',
                opacity: option.disabled ? 0.5 : 1,
                transition: 'background-color .15s, color .15s',
                maxWidth: '100%',
              }}
            >
              {showBadge ? (
                <span
                  aria-hidden="true"
                  style={{
                    flex: 'none',
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: 'var(--on-accent)',
                    color: 'var(--accent)',
                    fontFamily: 'var(--font-serif)',
                    fontSize: 12,
                    lineHeight: '20px',
                    textAlign: 'center',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {position + 1}
                </span>
              ) : null}
              <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{option.label}</span>
            </button>
          );
        })}
      </div>

      {other && otherPicked ? (
        <label
          htmlFor={otherFieldId}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            minHeight: 44,
            borderBottom: `1px solid ${otherFocus ? 'var(--accent)' : 'var(--line)'}`,
            fontFamily: 'var(--font-sans)',
            fontSize: 13,
            color: 'var(--chip-text)',
          }}
        >
          <span style={{ flex: 'none' }}>{other.label ?? 'Other'}:</span>
          <input
            id={otherFieldId}
            value={other.text}
            onChange={(e) => other.onTextChange(e.target.value)}
            onFocus={() => setOtherFocus(true)}
            onBlur={() => setOtherFocus(false)}
            placeholder={other.placeholder ?? 'Tell us'}
            maxLength={80}
            autoFocus
            style={{
              flex: 1,
              minWidth: 0,
              height: 44,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontFamily: 'var(--font-sans)',
              // 16px keeps iOS Safari from zooming the page on focus.
              fontSize: 16,
              color: 'var(--text)',
            }}
          />
        </label>
      ) : null}
    </div>
  );
}

export default ChoiceChips;
